/**
 * ProductForm - Reusable form component for creating/editing products
 */

import React, { useEffect, useState } from "react";
import { Form, Input, Select, InputNumber, Button, Space, Switch } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { getPartners } from "../../partners/services/partnerService.js";
import { logError } from "@/shared/utils/logger.js";

const { TextArea } = Input;
const { Option } = Select;

const CATEGORIES = [
  { value: "supplies", label: "Supplies" },
  { value: "services", label: "Services" },
  { value: "protection", label: "Protection" },
  { value: "packing", label: "Packing" },
  { value: "transport", label: "Transport" },
  { value: "other", label: "Other" },
];

const ProductForm = ({
  form,
  onFinish,
  onCancel,
  loading = false,
  initialValues = {},
}) => {
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);

  // Partners are payable suppliers; drivers are selected during operations.
  useEffect(() => {
    const loadPartners = async () => {
      setLoadingPartners(true);
      try {
        const result = await getPartners({ isActive: true });
        if (result.success) {
          setPartners(result.data || []);
        }
      } catch (error) {
        logError("Error loading partners", {
          error: error.message,
        });
      } finally {
        setLoadingPartners(false);
      }
    };
    loadPartners();
  }, []);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        category: "other",
        active: true,
        ...initialValues,
      }}
    >
      <Form.Item
        name="name"
        label="Product Name"
        rules={[
          { required: true, message: "Product name is required" },
          { min: 2, message: "Name must be at least 2 characters" },
          { max: 200, message: "Name must be less than 200 characters" },
        ]}
      >
        <Input placeholder="Enter product name" size="large" />
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
        rules={[
          {
            max: 1000,
            message: "Description must be less than 1000 characters",
          },
        ]}
      >
        <TextArea
          placeholder="Enter product description (optional)"
          rows={3}
          showCount
          maxLength={1000}
        />
      </Form.Item>

      <Form.Item
        name="unitPrice"
        label="Unit Price (R)"
        rules={[
          { required: true, message: "Unit price is required" },
          { type: "number", min: 0, message: "Price must be >= 0" },
        ]}
      >
        <InputNumber
          placeholder="0.00"
          style={{ width: "100%" }}
          size="large"
          min={0}
          step={0.01}
          precision={2}
          formatter={(value) =>
            `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/R\s?|(,*)/g, "")}
        />
      </Form.Item>

      <Form.Item
        name="category"
        label="Category"
        rules={[{ required: true, message: "Category is required" }]}
      >
        <Select
          placeholder="Select category"
          size="large"
          getPopupContainer={(trigger) => trigger.parentElement}
          style={{ width: "100%" }}
        >
          {CATEGORIES.map((cat) => (
            <Option key={cat.value} value={cat.value}>
              {cat.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="defaultSupplier"
        label="Default Supplier"
        tooltip="Payee for this product. Select Bubakii or a registered partner."
      >
        <Select
          placeholder="Select default supplier (optional)"
          size="large"
          loading={loadingPartners}
          allowClear
          getPopupContainer={(trigger) => trigger.parentElement}
        >
          <Option value="bubakii">Bubakii</Option>
          {String(initialValues.defaultSupplier || "").startsWith("DRV_") && (
            <Option value={initialValues.defaultSupplier} disabled>
              {initialValues.defaultSupplier} (legacy driver supplier)
            </Option>
          )}
          {partners.map((partner) => (
            <Option key={partner.id} value={partner.id}>
              {partner.name} ({partner.id})
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="sku" label="SKU">
        <Input placeholder="Enter SKU (optional)" size="large" />
      </Form.Item>

      <Form.Item
        name="active"
        label="Status"
        valuePropName="checked"
        initialValue={true}
      >
        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
            size="large"
          >
            Save Product
          </Button>
          {onCancel && (
            <Button icon={<CloseOutlined />} onClick={onCancel} size="large">
              Cancel
            </Button>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default ProductForm;






